// Read-only Graphite server metadata (metric names and render function documentation), retrieved
// through the GraphProxy controller which requires the caller's session cookie.  These are plain GET
// requests (not the JSON POST API) because GraphProxy proxies Graphite's own REST endpoints.

/**
 * Queries Graphite's metric name index. Supports wildcards, e.g. "stats.gauges.WEB5.*".
 * @param {String} query Metric path query.
 * @returns {Promise} Resolves with an array of { text, id, leaf (0/1), expandable (0/1) } objects.
 */
export function GraphiteFindMetrics(query)
{
	return fetch(appContext.appPath + "GraphProxy/Metrics?format=treejson&query=" + encodeURIComponent(query))
		.then(response =>
		{
			if (!response.ok)
				throw new Error("Metric lookup failed: HTTP " + response.status + " " + response.statusText);
			return response.json();
		});
}

let functionsPromise = null;
/**
 * Retrieves Graphite's render function metadata (name, signature, documentation, parameter definitions),
 * cached for the lifetime of the page.
 * @returns {Promise} Resolves with the raw object from Graphite's /functions endpoint, keyed by function name.
 */
export function GraphiteGetFunctions()
{
	if (!functionsPromise)
	{
		functionsPromise = fetch(appContext.appPath + "GraphProxy/Functions")
			.then(response =>
			{
				if (!response.ok)
					throw new Error("Function list failed: HTTP " + response.status + " " + response.statusText);
				return response.json();
			})
			.catch(err =>
			{
				functionsPromise = null; // Allow a retry on the next call.
				throw err;
			});
	}
	return functionsPromise;
}

let deepSearchPromise = null;
/**
 * Detects (once per page) whether the Graphite server supports "**" multi-level wildcards in
 * /metrics/find queries, enabling deep plain-text metric searching in the metric browser.
 * @returns {Promise} Resolves with true or false. Never rejects.
 */
export function GraphiteSupportsDeepSearch()
{
	if (!deepSearchPromise)
	{
		deepSearchPromise = GraphiteFindMetrics("**")
			.then(results => Array.isArray(results) && results.length > 0)
			.catch(() => false);
	}
	return deepSearchPromise;
}

/**
 * Converts a Graphite function docstring (reStructuredText-flavored) into readable plain text.
 * @param {String} description Raw description from the /functions endpoint.
 * @returns {Object} { summary, full } - summary is the first paragraph; full is the cleaned complete text.
 */
export function CleanFunctionDescription(description)
{
	if (!description)
		return { summary: "", full: "" };
	let text = description
		.replace(/\.\. code-block:: none\s*/g, "")
		.replace(/\.\. seealso::\s*/g, "See also: ")
		.replace(/\.\. note::\s*/g, "Note: ")
		.replace(/:py:func:`([^`<]+?)\s*<[^`>]*>`/g, "$1")
		.replace(/:py:func:`([^`]+)`/g, "$1")
		.replace(/``([^`]+)``/g, "$1")
		.replace(/`([^`]+)`_?/g, "$1");
	let paragraphs = text.split(/\r?\n\s*\r?\n/);
	let summary = (paragraphs[0] || "").replace(/\s+/g, " ").trim();
	return { summary, full: text.trim() };
}
